import * as React from 'react';
import {
  IconArrowRightSmall,
  IconMap as IconMapLegacy,
  IconPinWinery,
  IconSearch as IconSearchLegacy,
  IconWineGlass as IconWineGlassLegacy,
} from 'ui/icons';
import { IconChevronSmallRight, IconGrapes, IconMap, IconSearch, IconWineGlass } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Link, Typography } from 'omakase';
import { SearchBarOption, SearchNavEntityTypes } from 'utils/searchBar';
import { useMountEffect } from '@tock/hooks';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import clsx from 'clsx';

interface Props {
  entry: SearchBarOption;
  selected: boolean;
  onClick(entry: SearchBarOption): void;
  onKeyDown(event: React.KeyboardEvent<HTMLAnchorElement>): void;
  preventDefaultOnSelect?: boolean;
}

export const getIconForQueryCategory = (subCategory?: SearchNavEntityTypes) => {
  if (!subCategory) {
    return <IconMigrationUtil legacyIcon={<IconSearchLegacy />} newIcon={<IconSearch />} />;
  }

  switch (subCategory) {
    case SearchNavEntityTypes.WINE_STYLE:
      return <IconMigrationUtil legacyIcon={<IconWineGlassLegacy />} newIcon={<IconWineGlass />} />;

    case SearchNavEntityTypes.WINE_VARIETAL:
      return <IconMigrationUtil legacyIcon={<IconPinWinery />} newIcon={<IconGrapes />} />;
    case SearchNavEntityTypes.WINE_REGION:
      return <IconMigrationUtil legacyIcon={<IconMapLegacy />} newIcon={<IconMap />} />;
    default:
      return <IconMigrationUtil legacyIcon={<IconSearchLegacy />} newIcon={<IconSearch />} />;
  }
};

const SearchBarDesktopResult: React.FunctionComponent<Props> = ({
  entry,
  selected,
  onClick,
  onKeyDown,
  preventDefaultOnSelect,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const linkRef = React.useRef<any>();

  React.useEffect(() => {
    if (selected && linkRef.current) {
      const node = ReactDOM.findDOMNode(linkRef.current);
      if (node) {
        (node as HTMLAnchorElement).focus();
      }
    }
  }, [selected]);

  useMountEffect(() => {
    if (selected && linkRef.current) {
      const node = ReactDOM.findDOMNode(linkRef.current);
      if (node) {
        (node as HTMLAnchorElement).focus();
      }
    }
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (preventDefaultOnSelect) {
      event.preventDefault();
    }
    onClick(entry);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // NOTE: This handler is required because we're using the 'blur' event on
    // the associated input, which technically fires before 'click' at the
    // browser level. If we don't preventDefault(), this component will unmount
    // before the handleClick event can fire.
    event.preventDefault();
  };

  const isHeading = entry.category === SearchNavEntityTypes.HEADING;
  const className = isHeading
    ? 'SearchNav-destinationSearchHeading'
    : clsx('SearchNav-destinationSearchLink', { selected });
  const testIdValue = entry.primaryText.replace(/\W/g, '');

  if (entry.category === SearchNavEntityTypes.HEADING) {
    return (
      <li data-testid={'searchnav-heading_' + testIdValue} className={className}>
        <div className={'SearchNav-destinationSearchLinkArea'}>
          <div className="SearchNav-destinationSearchContent">
            <Typography
              color="fgDefault"
              className="SearchNav-destinationSearchHeadingName"
              variant="caption"
              component="span"
            >
              {entry.primaryText}
            </Typography>
          </div>
        </div>
      </li>
    );
  }

  if (entry.category === SearchNavEntityTypes.TEXT) {
    return (
      <li>
        <Link
          ref={linkRef}
          data-testid={'searchnav-querySeeAll_' + testIdValue}
          className={clsx(
            { selected },
            'SearchNav-destinationSearchLink SearchNav-destinationSearchLinkSeeAll'
          )}
          href={entry.href}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          onMouseDown={handleMouseDown}
          underline="none"
        >
          <Typography color="fgPrimaryBase" noWrap>
            {entry.primaryText}
          </Typography>
          <IconMigrationUtil
            legacyIcon={<IconArrowRightSmall />}
            newIcon={<IconChevronSmallRight />}
          />
        </Link>
      </li>
    );
  }

  if (entry.category === SearchNavEntityTypes.QUERY) {
    return (
      <li>
        <Link
          ref={linkRef}
          data-testid={'searchnav-querypopularsearches_' + testIdValue}
          className={clsx(
            { selected },
            'SearchNav-destinationSearchLink SearchNav-destinationSearchQuery'
          )}
          color="inherit"
          href={entry.href}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          onMouseDown={handleMouseDown}
          underline="none"
        >
          {getIconForQueryCategory(entry.subCategory)}
          <Typography noWrap>{entry.primaryText}</Typography>
        </Link>
      </li>
    );
  }

  if (entry.category === SearchNavEntityTypes.CUISINE) {
    return (
      <li>
        <Link
          ref={linkRef}
          data-testid={'searchnav-queryCuisine_' + testIdValue}
          className={clsx(
            { selected },
            'SearchNav-destinationSearchLink SearchNav-destinationSearchQuery'
          )}
          href={entry.href}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          onMouseDown={handleMouseDown}
          underline="none"
        >
          <Typography
            color="fgDefault"
            component="span"
            className="SearchNav-destinationSearchLinkName"
            variant="body02"
            noWrap
          >
            {entry.primaryText}
          </Typography>
        </Link>
      </li>
    );
  }

  if (
    entry.category === SearchNavEntityTypes.WINE_STYLE ||
    entry.category === SearchNavEntityTypes.WINE_VARIETAL
  ) {
    return (
      <li>
        <Link
          ref={linkRef}
          data-testid={'searchnav-queryDestination_' + testIdValue}
          className={clsx(
            { selected },
            'SearchNav-destinationSearchLink SearchNav-destinationSearchQuery'
          )}
          color="inherit"
          href={entry.href}
          onClick={handleClick}
          onKeyDown={onKeyDown}
          onMouseDown={handleMouseDown}
          underline="none"
        >
          <div className={'SearchNav-destinationSearchLinkArea'}>
            {getIconForQueryCategory(entry.category)}
            <div className="SearchNav-destinationSearchContent">
              <Typography
                color="fgDefault"
                component="span"
                className="SearchNav-destinationSearchLinkName"
                variant="body02"
              >
                {entry.primaryText}
              </Typography>
            </div>
          </div>
        </Link>
      </li>
    );
  }

  return (
    <li>
      <Link
        ref={linkRef}
        data-testid={'searchnav-queryDestination_' + testIdValue}
        className={clsx(
          { selected },
          'SearchNav-destinationSearchLink SearchNav-destinationSearchQuery'
        )}
        color="inherit"
        href={entry.href}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        onMouseDown={handleMouseDown}
        underline="none"
      >
        <div className={'SearchNav-destinationSearchLinkArea'}>
          <div className="SearchNav-destinationSearchContent">
            <Typography
              component="span"
              color="fgDefault"
              className="SearchNav-destinationSearchLinkName"
              variant="body02"
            >
              {entry.primaryText}
            </Typography>
            {_.size(entry.secondaryText) > 0 && (
              <Typography
                color="fgSubtle"
                component="span"
                className="SearchNav-destinationSearchLinkLocation"
                variant="caption"
              >
                {entry.secondaryText}
              </Typography>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
};

export default SearchBarDesktopResult;
